/**
 * Grounds — the six colour surfaces the admin can sit on.
 *
 * A ground is a pure token swap: canvas / panel / raised / sunk, the two line
 * weights, the three text tiers, the ember accent and the three semantic tones.
 * Nothing else changes between them, which is what lets one component tree
 * render on all six. Tokens and values are the ones from the reskin prototype
 * (deeliver-dash-skin, the `.g-*` blocks) — keep the two in step if either moves.
 *
 * Four darks then two lights. `mode` is what MUI needs to know: it drives the
 * contrast logic inside components we don't style ourselves (menus, dialogs,
 * data grids), so every ground must declare it.
 */

export type GroundId = 'ink' | 'slate' | 'storm' | 'mulberry' | 'mist' | 'bone';

export type GroundTokens = {
  id: GroundId;
  name: string;
  mode: 'light' | 'dark';

  /** Page background. */
  canvas: string;
  /** Card / panel surface. */
  panel: string;
  /** A surface sitting on a panel: inputs, hover rows, inner cells. */
  raised: string;
  /** A surface sunk below the canvas: the rail. */
  sunk: string;

  line: string;
  lineSoft: string;

  tx: string;
  tx2: string;
  tx3: string;

  /** The accent. */
  ember: string;
  emberBg: string;
  emberLine: string;

  ok: string;
  warn: string;
  crit: string;

  hover: string;
  track: string;
  warnBg: string;
  warnLine: string;
  okBg: string;
  okLine: string;
  shade: string;
};

const ink: GroundTokens = {
  id: 'ink', name: 'Deep ink', mode: 'dark',
  canvas: '#0A0810', panel: '#120F1A', raised: '#1A1626', sunk: '#0E0B15',
  line: '#2A2438', lineSoft: '#201B2C',
  tx: '#F2EFF7', tx2: '#A09AB2', tx3: '#6B6580',
  ember: '#FF6132', emberBg: '#1D1218', emberLine: '#5C2A22',
  ok: '#35D3AE', warn: '#F3B54A', crit: '#FF3560',
  hover: '#171326', track: '#221C33',
  warnBg: '#1C1607', warnLine: '#4A3A18', okBg: '#101B21', okLine: '#1F5152', shade: '#000000',
};

const slate: GroundTokens = {
  id: 'slate', name: 'Cool slate', mode: 'dark',
  canvas: '#090B0F', panel: '#11151B', raised: '#191F27', sunk: '#0D1015',
  line: '#252D38', lineSoft: '#1C232C',
  tx: '#EEF2F7', tx2: '#96A1B0', tx3: '#606C7A',
  ember: '#FF6A2B', emberBg: '#181310', emberLine: '#553017',
  ok: '#31D0A5', warn: '#F5B845', crit: '#FF3A5E',
  hover: '#151A21', track: '#1E2530',
  warnBg: '#191505', warnLine: '#463914', okBg: '#0E1C1A', okLine: '#1C5147', shade: '#000000',
};

const storm: GroundTokens = {
  id: 'storm', name: 'Storm blue', mode: 'dark',
  canvas: '#151B23', panel: '#1F2733', raised: '#29323F', sunk: '#1A212A',
  line: '#394452', lineSoft: '#2C3542',
  tx: '#EEF2F8', tx2: '#9FAAB9', tx3: '#717C8C',
  ember: '#FF6D33', emberBg: '#2A1B15', emberLine: '#6C321A',
  ok: '#33D5A4', warn: '#F5B94A', crit: '#FF4665',
  hover: '#1D2531', track: '#313A48',
  warnBg: '#26200A', warnLine: '#4F421A', okBg: '#13241F', okLine: '#245549', shade: '#000000',
};

const mulberry: GroundTokens = {
  id: 'mulberry', name: 'Mulberry', mode: 'dark',
  canvas: '#1A1521', panel: '#251E2E', raised: '#30283A', sunk: '#1F1927',
  line: '#433B51', lineSoft: '#342D3F',
  tx: '#F3EFF8', tx2: '#ADA3BA', tx3: '#80778E',
  ember: '#FF6B38', emberBg: '#2D1B19', emberLine: '#703120',
  ok: '#38D6AC', warn: '#F4B851', crit: '#FF476C',
  hover: '#221C2A', track: '#3B3246',
  warnBg: '#2A2213', warnLine: '#564720', okBg: '#16251F', okLine: '#27574B', shade: '#000000',
};

const mist: GroundTokens = {
  id: 'mist', name: 'Mist', mode: 'light',
  canvas: '#EDEAF2', panel: '#FFFFFF', raised: '#F6F4FA', sunk: '#E0DAEB',
  line: '#CBC3DC', lineSoft: '#D8D2E5',
  tx: '#13101B', tx2: '#4A4458', tx3: '#665F73',
  ember: '#C2400C', emberBg: '#FCEBE3', emberLine: '#F1BD9F',
  ok: '#0A7A5E', warn: '#86580A', crit: '#C11C42',
  hover: '#E8E4F0', track: '#DAD3E6',
  warnBg: '#F9F0DB', warnLine: '#DFC894', okBg: '#E4F2EC', okLine: '#A3D3C1', shade: '#ADA5BC',
};

const bone: GroundTokens = {
  id: 'bone', name: 'Bone', mode: 'light',
  canvas: '#F0EDE7', panel: '#FFFFFF', raised: '#F8F6F2', sunk: '#E2DCD1',
  line: '#D0C8BA', lineSoft: '#DDD6CA',
  tx: '#16120F', tx2: '#554D45', tx3: '#6E655B',
  ember: '#BE3D08', emberBg: '#FCEBE1', emberLine: '#F0C0A2',
  ok: '#0B7A57', warn: '#85570A', crit: '#C11E3F',
  hover: '#EBE7DF', track: '#DCD5C9',
  warnBg: '#FAF0D9', warnLine: '#E0C88D', okBg: '#E5F2EB', okLine: '#A5D3BF', shade: '#B9AF9F',
};

/** Cycle order for the app-bar button: the four darks, then the two lights. */
export const GROUNDS: readonly GroundTokens[] = [ink, slate, storm, mulberry, mist, bone];

export const DEFAULT_GROUND: GroundId = 'ink';

export function getGround(id: string | null | undefined): GroundTokens {
  return GROUNDS.find((g) => g.id === id) ?? GROUNDS[0];
}

export function nextGround(id: GroundId): GroundTokens {
  const i = GROUNDS.findIndex((g) => g.id === id);
  return GROUNDS[(i + 1) % GROUNDS.length];
}

/** The ground a fresh browser lands on for a saved light/dark preference. */
export function defaultGroundForMode(mode: 'light' | 'dark'): GroundId {
  return mode === 'light' ? 'mist' : DEFAULT_GROUND;
}

/** Type stacks. Archivo for display + body, JetBrains Mono for data. */
export const FONT_DISPLAY = '"Archivo", "Helvetica Neue", Arial, sans-serif';
export const FONT_BODY = FONT_DISPLAY;
export const FONT_DATA = '"JetBrains Mono", ui-monospace, "SFMono-Regular", Consolas, monospace';

/**
 * The same tokens as CSS custom properties, so any sx/CSS can read
 * `var(--canvas)` etc. and re-theme with the ground without a hook.
 * Applied on :root by ThemeModeProvider.
 */
export function groundCssVars(g: GroundTokens): Record<string, string> {
  return {
    '--canvas': g.canvas, '--panel': g.panel, '--raised': g.raised, '--sunk': g.sunk,
    '--line': g.line, '--line-soft': g.lineSoft,
    '--tx': g.tx, '--tx2': g.tx2, '--tx3': g.tx3,
    '--ember': g.ember, '--ember-bg': g.emberBg, '--ember-line': g.emberLine,
    '--ok': g.ok, '--warn': g.warn, '--crit': g.crit,
    '--hover': g.hover, '--track': g.track,
    '--warn-bg': g.warnBg, '--warn-line': g.warnLine,
    '--ok-bg': g.okBg, '--ok-line': g.okLine, '--shade': g.shade,
    '--r-panel': '10px', '--r-ctl': '6px',
    '--f-disp': FONT_DISPLAY, '--f-body': FONT_BODY, '--f-data': FONT_DATA,
  };
}
